// O e-mail do CONVITE para uma pesquisa de satisfação (000320 — o G1 do Plano de
// Melhorias da Equipe de Produtos), num lugar só.
//
// Dois leitores, e é por isso que o módulo mora aqui e não tem import nenhum: a
// Edge Function `enviar-convites-de-pesquisa` monta o e-mail que sai, e a tela da
// equipe (`AdminCatalogoProdutoPesquisas.tsx`) monta a PRÉVIA do mesmo e-mail
// antes de alguém mandar para 34 mil pessoas. Duas montagens divergiriam, e a
// prévia mentiria sobre o que chega — o mesmo arranjo do `tipo-de-arquivo.ts`.
// Só `Intl` e string: existe nos dois ambientes.
//
// ---------------------------------------------------------------------------
// A cara é a dos 13 e-mails do Auth — e as constantes são uma CÓPIA vigiada
// ---------------------------------------------------------------------------
// O molde da casa é `supabase/emails/layout.mjs`, e as razões de cada escolha
// estão lá (tabela, estilo em linha, cor fixa, o logo hospedado com o navy
// pintado, o botão com `<font color>`). Este módulo não o importa porque a Edge
// Function é empacotada a partir de `supabase/functions/`, e o `layout.mjs`
// mora fora — a mesma fronteira que fez o `avisar-documento-pronto` escrever o
// seu. A diferença é que aqui a cópia é VIGIADA: o teste deste módulo importa o
// `layout.mjs` e exige as mesmas cores, o mesmo logo e os mesmos endereços.
//
// ---------------------------------------------------------------------------
// O que o e-mail NÃO faz, de propósito
// ---------------------------------------------------------------------------
//   · o número clicado não grava nada: ele abre a página com a nota marcada, e
//     o aluno confirma. Leitor de links corporativo segue todo link de todo
//     e-mail, e voto gravado no GET seria a varredura dele virando NPS;
//   · a escala não tem cor por faixa: vermelho no 0 e verde no 10 empurram a
//     resposta, e o NPS mede a opinião, não o desenho;
//   · nenhum link passa pelo rastreio do provedor (`clicktracking="off"`, e o
//     envio desliga o rastreio de clique e de abertura): o link reescrito para
//     outro domínio é a assinatura de golpe, e o token iria parar no servidor
//     de outra empresa.

export const CORES = {
  navy: "#102850",
  azulMedio: "#183c77",
  laranja: "#ff9933",
  laranjaTexto: "#b35f00",
  fundo: "#f5f7fb",
  superficie: "#ffffff",
  tinta: "#17233d",
  cinza: "#5d6673",
  borda: "#e3e7ef",
  branco: "#ffffff",
};

export const LOGO_URL =
  "https://wynpioiguztlbhaynwnp.supabase.co/storage/v1/object/public/marca/cf-logo-email.png";
export const LOGO_LARGURA = 240;
export const LOGO_ALTURA = 64;
export const FALE_CONOSCO = "https://ec.cfaci.me/site/";
export const HUB = "https://app.contabilidadefacilitada.com";

const FONTE_TITULO = "'Sora','Inter',Arial,Helvetica,sans-serif";
const FONTE_TEXTO = "'Inter',Arial,Helvetica,sans-serif";
const GOOGLE_FONTS =
  "https://fonts.googleapis.com/css2?family=Sora:wght@700&family=Inter:wght@400;600;700&display=swap";

/** O token do link: 32 hex (`gen_random_uuid` sem hífen, 000320). */
export const FORMA_DO_TOKEN = /^[0-9a-f]{32}$/;

export type DadosDoConvite = {
  token: string;
  /** O nome do perfil — sai só o primeiro. */
  nome: string | null;
  cursoNome: string;
  alvo: "curso" | "professor";
  pergunta: string;
  /** ISO; nulo = aberta até alguém encerrar. */
  fechaEm: string | null;
};

/** Onde o link abre: a página do hub e o descadastro de um clique (Edge Function). */
export type Bases = { hub: string; funcoes: string };

export type EmailDoConvite = {
  assunto: string;
  preheader: string;
  html: string;
  texto: string;
  /** Os cabeçalhos do descadastro de um clique (RFC 8058). */
  cabecalhos: Record<string, string>;
};

/**
 * Escapa o que vai para o HTML. O nome é texto que o aluno edita, e o curso e a
 * pergunta são texto que a equipe edita — os três vão parar na caixa de outra
 * pessoa. É a regra do `avisar-documento-pronto` e dos documentos imprimíveis:
 * TODO valor é escapado.
 */
export function esc(v: string): string {
  return v
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/** O primeiro nome, para o e-mail não começar com 74 caracteres. */
export function primeiroNome(nome: string | null): string {
  const limpo = (nome ?? "").trim();
  return limpo ? limpo.split(/\s+/)[0] : "";
}

/** A data do fim, no dia de São Paulo — o instante gravado é o fim do dia de lá. */
export function diaDoFim(iso: string): string {
  return new Intl.DateTimeFormat("pt-BR", {
    timeZone: "America/Sao_Paulo",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(new Date(iso));
}

/** Os endereços do convite. O token é conferido: link com lixo nunca é montado. */
export function enderecosDoConvite(token: string, b: Bases) {
  if (!FORMA_DO_TOKEN.test(token)) throw new Error("token fora da forma — o link não é montado");
  const pagina = `${b.hub}/pesquisa/${token}`;
  return {
    pagina,
    nota: (n: number) => `${pagina}?nota=${n}`,
    descadastrar: `${pagina}?descadastrar=1`,
    umClique: `${b.funcoes}/descadastrar-pesquisas?t=${token}`,
  };
}

function paragrafo(html: string, extra = ""): string {
  return `<p style="margin:0 0 14px;font-family:${FONTE_TEXTO};font-size:16px;line-height:1.6;color:${CORES.tinta};${extra}">${html}</p>`;
}

/**
 * A escala de 0 a 10 como ONZE LINKS. Largura em porcentagem, e não em pixel:
 * onze caixas de 36px não cabem nos ~316px de conteúdo de um celular, e o e-mail
 * transbordaria justamente onde ele é mais lido.
 */
function escala(nota: (n: number) => string): string {
  const celulas = Array.from({ length: 11 }, (_, n) => `
                <td width="9%" align="center" style="padding:0 2px;">
                  <a href="${esc(nota(n))}" clicktracking="off" style="display:block;padding:11px 0;border:1px solid ${CORES.borda};border-radius:8px;background-color:${CORES.superficie};font-family:${FONTE_TEXTO};font-size:16px;line-height:1;font-weight:700;color:${CORES.navy};text-decoration:none;"><font color="${CORES.navy}">${n}</font></a>
                </td>`).join("");
  return `
            <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="margin:6px 0 6px;">
              <tr>${celulas}
              </tr>
            </table>
            <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="margin:0 0 18px;">
              <tr>
                <td align="left" style="font-family:${FONTE_TEXTO};font-size:12px;line-height:1.4;color:${CORES.cinza};">0 = não recomendaria</td>
                <td align="right" style="font-family:${FONTE_TEXTO};font-size:12px;line-height:1.4;color:${CORES.cinza};">10 = recomendaria com certeza</td>
              </tr>
            </table>`;
}

/** O botão pill laranja com o texto navy — o do `layout.mjs`. */
function botao(href: string, rotulo: string): string {
  return `
            <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="margin:0;">
              <tr><td height="8" style="height:8px;line-height:8px;font-size:1px;mso-line-height-rule:exactly;">&nbsp;</td></tr>
              <tr>
                <td align="left">
                  <table role="presentation" cellpadding="0" cellspacing="0" border="0"><tr>
                  <td class="cta-td" align="center" bgcolor="${CORES.laranja}" style="border-radius:999px;background-color:${CORES.laranja};">
                    <a class="cta-a" href="${esc(href)}" clicktracking="off" style="display:inline-block;padding:15px 30px;font-family:${FONTE_TEXTO};font-size:16px;line-height:1.2;font-weight:700;color:${CORES.navy};text-decoration:none;border-radius:999px;"><font color="${CORES.navy}">${esc(rotulo)}</font></a>
                  </td>
                  </tr></table>
                </td>
              </tr>
              <tr><td height="22" style="height:22px;line-height:22px;font-size:1px;mso-line-height-rule:exactly;">&nbsp;</td></tr>
            </table>`;
}

/**
 * Monta o convite. O que muda entre os dois alvos é o que o aluno faz: na de
 * CURSO ele escolhe a nota no próprio e-mail; na de PROFESSOR são várias notas
 * (uma por docente da turma), e o e-mail leva à página.
 */
export function montarConvite(d: DadosDoConvite, b: Bases = { hub: HUB, funcoes: "" }): EmailDoConvite {
  const links = enderecosDoConvite(d.token, b);
  const nome = primeiroNome(d.nome);
  const ola = nome ? `Olá, ${nome}!` : "Olá!";
  const doCurso = d.alvo === "curso";
  const titulo = doCurso ? "Uma pergunta rápida sobre o seu curso" : "Como foram as aulas dos seus professores?";
  const intro = doCurso
    ? "A equipe da Contabilidade Facilitada quer saber como está sendo a sua experiência. É uma pergunta só, e leva menos de um minuto."
    : "A equipe da Contabilidade Facilitada quer saber como foram as aulas dos professores da sua turma. Você dá uma nota a cada um, e leva poucos minutos.";
  const fim = d.fechaEm ? `A pesquisa fica aberta até ${diaDoFim(d.fechaEm)}.` : "";
  const promessa = doCurso
    ? "A equipe vê a sua nota e o seu comentário — não o seu nome."
    : "A equipe vê a sua nota e o seu comentário — não o seu nome. Cada professor vê a nota e o comentário que recebeu, também sem o seu nome.";
  const assunto = doCurso ? `Sua opinião sobre o curso ${d.cursoNome}` : `Avalie os professores da sua turma — ${d.cursoNome}`;
  const preheader = `Leva ${doCurso ? "menos de um minuto" : "poucos minutos"}: ${d.pergunta}`.slice(0, 140);

  const acao = doCurso
    ? `${escala(links.nota)}
            ${paragrafo(`Escolha um número: a página abre com ele marcado, e você confirma — lá também dá para deixar um comentário. <a href="${esc(links.pagina)}" clicktracking="off" style="color:${CORES.azulMedio};text-decoration:underline;">Prefere abrir a pesquisa?</a>`, `font-size:14px;color:${CORES.cinza};`)}`
    : botao(links.pagina, "Avaliar os professores");

  const html = `<!DOCTYPE html>
<html lang="pt-BR" xmlns="http://www.w3.org/1999/xhtml" xmlns:v="urn:schemas-microsoft-com:vml" xmlns:o="urn:schemas-microsoft-com:office:office">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <meta http-equiv="X-UA-Compatible" content="IE=edge">
  <meta name="x-apple-disable-message-reformatting">
  <meta name="color-scheme" content="light">
  <meta name="supported-color-schemes" content="light">
  <title>${esc(assunto)}</title>
  <!--[if mso]>
  <noscript><xml><o:OfficeDocumentSettings><o:PixelsPerInch>96</o:PixelsPerInch></o:OfficeDocumentSettings></xml></noscript>
  <style>td,p,a,span,h1,h2{font-family:Arial,Helvetica,sans-serif !important;} .cta-td{padding:14px 28px !important;}</style>
  <![endif]-->
  <!--[if !mso]><!-- -->
  <link href="${GOOGLE_FONTS}" rel="stylesheet">
  <!--<![endif]-->
  <style>
    body { margin: 0; padding: 0; background-color: ${CORES.fundo}; -webkit-text-size-adjust: 100%; }
    table { border-collapse: collapse; }
    img { border: 0; line-height: 100%; outline: none; text-decoration: none; -ms-interpolation-mode: bicubic; }
    [data-ogsc] .cta-a { color: ${CORES.navy} !important; }
    [data-ogsb] .cta-td { background-color: ${CORES.laranja} !important; }
    @media only screen and (max-width: 620px) {
      .caixa { width: 100% !important; }
      .miolo { padding: 28px 18px 26px !important; }
      .cabecalho { padding: 22px 22px !important; }
      .titulo { font-size: 23px !important; }
    }
  </style>
</head>
<body style="margin:0;padding:0;background-color:${CORES.fundo};">
  <div style="display:none;max-height:0;overflow:hidden;mso-hide:all;font-size:1px;line-height:1px;color:${CORES.fundo};">${esc(preheader)}&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;</div>
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" bgcolor="${CORES.fundo}" style="background-color:${CORES.fundo};">
    <tr>
      <td align="center" style="padding:32px 12px 40px;">
        <!--[if mso]><table role="presentation" width="600" cellpadding="0" cellspacing="0" border="0"><tr><td><![endif]-->
        <table role="presentation" class="caixa" width="600" cellpadding="0" cellspacing="0" border="0" style="width:600px;max-width:600px;">
        <tr>
          <td class="cabecalho" align="left" bgcolor="${CORES.navy}" style="background-color:${CORES.navy};border-radius:16px 16px 0 0;padding:26px 40px;">
            <img src="${LOGO_URL}" width="${LOGO_LARGURA}" height="${LOGO_ALTURA}" alt="Contabilidade Facilitada" style="display:block;width:${LOGO_LARGURA}px;height:${LOGO_ALTURA}px;border:0;font-family:${FONTE_TITULO};font-size:18px;font-weight:700;color:${CORES.branco};">
          </td>
        </tr>
        <tr>
          <td class="miolo" bgcolor="${CORES.superficie}" style="background-color:${CORES.superficie};padding:36px 40px 32px;border-left:1px solid ${CORES.borda};border-right:1px solid ${CORES.borda};">
            <p style="margin:0 0 10px;font-family:${FONTE_TEXTO};font-size:12px;line-height:1.4;font-weight:700;letter-spacing:0.08em;text-transform:uppercase;color:${CORES.laranjaTexto};">${esc(d.cursoNome)}</p>
            <h1 class="titulo" style="margin:0 0 16px;font-family:${FONTE_TITULO};font-size:26px;line-height:1.2;font-weight:700;letter-spacing:-0.02em;color:${CORES.navy};">${esc(titulo)}</h1>
            ${paragrafo(esc(ola))}
            ${paragrafo(esc(intro))}
            ${paragrafo(`<strong>${esc(d.pergunta)}</strong>`)}
            ${acao}
            ${fim ? paragrafo(esc(fim), `font-size:14px;color:${CORES.cinza};`) : ""}
            <p style="margin:0;font-family:${FONTE_TEXTO};font-size:14px;line-height:1.6;color:${CORES.cinza};">${esc(promessa)}</p>
          </td>
        </tr>
        <tr>
          <td bgcolor="${CORES.laranja}" height="5" style="background-color:${CORES.laranja};height:5px;line-height:5px;font-size:1px;mso-line-height-rule:exactly;border-radius:0 0 16px 16px;">&nbsp;</td>
        </tr>
        <tr>
          <td align="center" style="padding:24px 12px 0;font-family:${FONTE_TEXTO};font-size:13px;line-height:1.6;color:${CORES.cinza};">
            <p style="margin:0;">Você recebeu este e-mail porque tem o curso ${esc(d.cursoNome)} na Contabilidade Facilitada.
              Não quer receber pesquisas por e-mail? <a href="${esc(links.descadastrar)}" clicktracking="off" style="color:${CORES.azulMedio};font-weight:600;text-decoration:underline;">Descadastre-se</a>.</p>
          </td>
        </tr>
        <tr>
          <td align="center" style="padding:14px 12px 0;font-family:${FONTE_TEXTO};font-size:13px;line-height:1.6;color:${CORES.cinza};">
            <p style="margin:0 0 6px;">Precisa de ajuda? <a href="${FALE_CONOSCO}" style="color:${CORES.azulMedio};font-weight:600;text-decoration:underline;">Fale conosco</a>.</p>
            <p style="margin:0;font-family:${FONTE_TITULO};font-size:13px;font-weight:700;color:${CORES.navy};">Contabilidade Facilitada <span style="color:${CORES.laranja};">&middot;</span> Conta com a gente!</p>
          </td>
        </tr>
        </table>
        <!--[if mso]></td></tr></table><![endif]-->
      </td>
    </tr>
  </table>
</body>
</html>
`;

  // O texto puro não é enfeite: cliente que bloqueia HTML mostra ele, e sem ele
  // o e-mail chega vazio — a mesma razão dos 13 do Auth.
  const texto = [
    ola,
    "",
    intro,
    "",
    d.pergunta,
    "",
    doCurso ? "Responda aqui (leva menos de um minuto):" : "Avalie os professores aqui:",
    links.pagina,
    ...(fim ? ["", fim] : []),
    "",
    promessa,
    "",
    "—",
    `Você recebeu este e-mail porque tem o curso ${d.cursoNome} na Contabilidade Facilitada.`,
    `Para não receber mais pesquisas por e-mail: ${links.descadastrar}`,
  ].join("\n");

  // Sem a base das funções (a prévia da tela), não há cabeçalho de um clique.
  const cabecalhos: Record<string, string> = b.funcoes
    ? { "List-Unsubscribe": `<${links.umClique}>`, "List-Unsubscribe-Post": "List-Unsubscribe=One-Click" }
    : {};

  return { assunto, preheader, html, texto, cabecalhos };
}

// ---------------------------------------------------------------------------
// O desfecho de um envio, CLASSIFICADO
// ---------------------------------------------------------------------------
// A pergunta que decide é a de sempre nesta casa: "o que muda entre uma
// tentativa e a seguinte?". Endereço recusado não muda — conta tentativa, e na
// terceira o convite sai da fila. Chave, limite, provedor fora do ar ou pedido
// malformado não são culpa do convite: não contam, o erro fica escrito, e a
// rodada PARA — senão uma hora de SendGrid fora queimaria as tentativas da fila
// inteira em cinco minutos.
export type Desfecho =
  | { ok: true }
  | { ok: false; conta: boolean; parar: boolean; erro: string };

/** Tira qualquer endereço de e-mail da mensagem: o erro fica numa tabela que a equipe lê. */
function semEndereco(t: string): string {
  return t.replace(/[^\s@<>"']+@[^\s@<>"']+/g, "<endereço>");
}

export function classificarEnvio(status: number, corpo: string): Desfecho {
  if (status >= 200 && status < 300) return { ok: true };
  let mensagem = "";
  let campoDoDestinatario = false;
  try {
    const j = JSON.parse(corpo) as { errors?: Array<{ message?: string; field?: string | null }> };
    const erros = j.errors ?? [];
    mensagem = erros.map((e) => e.message ?? "").filter(Boolean).join("; ");
    campoDoDestinatario = erros.some((e) => /personalizations\.\d+\.to/.test(e.field ?? ""));
  } catch {
    mensagem = corpo;
  }
  const texto = semEndereco(mensagem).slice(0, 200);
  if (status === 400 && (campoDoDestinatario || /e-?mail address|invalid email|does not contain a valid address/i.test(mensagem))) {
    return { ok: false, conta: true, parar: false, erro: `sendgrid 400: ${texto || "endereço recusado"}` };
  }
  if (status === 401 || status === 403) {
    return { ok: false, conta: false, parar: true, erro: `sendgrid ${status}: a chave do provedor foi recusada` };
  }
  if (status === 429) return { ok: false, conta: false, parar: true, erro: "sendgrid 429: limite de envio do provedor" };
  if (status >= 500) return { ok: false, conta: false, parar: true, erro: `sendgrid ${status}: o provedor está fora do ar` };
  if (status === 0) return { ok: false, conta: false, parar: true, erro: `sem resposta do provedor: ${texto || "rede"}` };
  // Outro 4xx: o PEDIDO está errado, e todo convite falharia igual.
  return { ok: false, conta: false, parar: true, erro: `sendgrid ${status}: ${texto || "pedido recusado"}` };
}
